// ============================================================
// useGmNotes - the GM's notes: one page of text, editable in play.
//
// Kept in `gmDesk/notes`, which the rules let the GM alone read - so a
// secret written here never reaches a player's machine. Typing shows at
// once and is saved a moment after the last keystroke (`flush` saves now,
// for a field being left).
//
// WHAT IS TYPED WINS over what is read while it is unsaved: a read that
// arrives between a keystroke and its write landing is the page as it was,
// and taking it would eat the last words typed.
//
// Outside a session the page is local to this screen. A seat that is not
// the GM's listens to nothing and is told so (`mine` false).
// ============================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { diag, errorSummary } from '../diagnostics/log';

/** How long after the last keystroke the page is saved, in ms. */
export const NOTES_SAVE_DELAY_MS = 800;
export const MAX_NOTES = 20000;

interface NotesSource {
  onGmNotesUpdate(sessionId: string, callback: (text: string | null) => void): () => void;
  setGmNotes(sessionId: string, text: string): Promise<void>;
}

interface Params {
  sessionId: string | null | undefined;
  isGm: boolean;
  mp: NotesSource;
}

export interface GmNotes {
  /** Whether this seat has the page at all: the GM, or anyone outside a session. */
  mine: boolean;
  text: string;
  setText(text: string): void;
  /** Save now what has been typed, if anything is unsaved. */
  flush(): void;
}

/** What is typed and not yet known to be stored. */
interface Unsaved {
  timer: ReturnType<typeof setTimeout> | null;
  /** Writes sent and not yet answered. */
  inFlight: number;
  latest: string;
}

const stopTimer = (unsaved: Unsaved) => {
  if (unsaved.timer !== null) clearTimeout(unsaved.timer);
  unsaved.timer = null;
};

/** The stored page, taken whenever nothing typed here is still unsaved. */
function useStoredNotes(sessionId: string | null, mp: NotesSource, unsaved: Unsaved) {
  const state = useState('');
  const setText = state[1];
  useEffect(() => {
    if (!sessionId) return undefined;
    let live = true;
    const unsub = mp.onGmNotesUpdate(sessionId, (stored) => {
      // Never taken over unsaved typing, and an absent page is simply empty - nothing is written for it.
      if (!live || unsaved.timer !== null || unsaved.inFlight > 0) return;
      setText(stored ?? '');
    });
    return () => {
      live = false;
      unsub();
      // The page belonged to the session just left.
      setText('');
    };
  }, [sessionId, mp, unsaved, setText]);
  return state;
}

/** Send what was typed last, now. */
function useSave(sessionId: string | null, mp: NotesSource, unsaved: Unsaved) {
  return useCallback(() => {
    stopTimer(unsaved);
    if (!sessionId) return;
    unsaved.inFlight += 1;
    mp.setGmNotes(sessionId, unsaved.latest)
      // Said, never thrown - and never with the text: the diagnostic log is readable by the table.
      .catch((e) => diag.error('gmDesk', 'the notes did not save', errorSummary(e)))
      .finally(() => {
        unsaved.inFlight -= 1;
      });
  }, [sessionId, mp, unsaved]);
}

export default function useGmNotes({ sessionId, isGm, mp }: Params): GmNotes {
  // The session whose page this seat keeps: the GM's own, else none.
  const kept = sessionId && isGm ? sessionId : null;
  // One record of what is unsaved PER SESSION: a save still in flight to the session just left
  // must not make this one ignore its own first read (and then save the old page over it).
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `kept` is the key, not an input
  const unsaved = useMemo<Unsaved>(() => ({ timer: null, inFlight: 0, latest: '' }), [kept]);
  const [text, setTextState] = useStoredNotes(kept, mp, unsaved);
  const save = useSave(kept, mp, unsaved);

  const setText = useCallback(
    (next: string) => {
      const held = next.slice(0, MAX_NOTES);
      setTextState(held);
      if (!kept) return;
      unsaved.latest = held;
      stopTimer(unsaved);
      unsaved.timer = setTimeout(save, NOTES_SAVE_DELAY_MS);
    },
    [kept, save, unsaved, setTextState],
  );

  const flush = useCallback(() => {
    if (unsaved.timer !== null) save();
  }, [save, unsaved]);

  // Leaving the session (or the app) with words unsaved: save them on the way out.
  useEffect(() => flush, [flush]);

  return { mine: !sessionId || isGm, text, setText, flush };
}
